import { apiClient } from '../../../lib/apiClient';

export interface RecipeIngredient {
  item: { id: string; name: string; sku: string; unit: string; minStock?: number; cost?: number };
  qty: number;
  unit: string;
  wastePct?: number;
  ingredientCost?: number;
  notes?: string;
}

export interface ProductRecipe {
  _id: string;
  productId: string;
  name?: string;
  ingredients: RecipeIngredient[];
  active: boolean;
  createdAt: string;
  totalCost?: number;
  productPrice?: number;
  foodCostPct?: number;
  grossMargin?: number;
  foodCostStatus?: 'good' | 'warning' | 'danger';
  suggestedPrice30?: number;
  suggestedPrice35?: number;
}

export interface UpsertRecipePayload {
  productId: string;
  name?: string;
  ingredients: {
    itemId: string;
    qty: number;
    unit: string;
    wastePct?: number;
    notes?: string;
  }[];
}

const mapBackendToFrontend = (recipe: any): ProductRecipe => {
  return {
    _id: String(recipe.id),
    productId: String(recipe.product_id),
    name: recipe.name ?? undefined,
    active: !!recipe.active,
    createdAt: recipe.created_at,
    totalCost: recipe.total_cost !== undefined ? Number(recipe.total_cost) : undefined,
    productPrice: recipe.product_price !== undefined ? Number(recipe.product_price) : undefined,
    foodCostPct: recipe.food_cost_pct !== undefined ? Number(recipe.food_cost_pct) : undefined,
    grossMargin: recipe.gross_margin !== undefined ? Number(recipe.gross_margin) : undefined,
    foodCostStatus: recipe.food_cost_status,
    suggestedPrice30: recipe.suggested_price_30 !== undefined ? Number(recipe.suggested_price_30) : undefined,
    suggestedPrice35: recipe.suggested_price_35 !== undefined ? Number(recipe.suggested_price_35) : undefined,
    ingredients: (recipe.ingredients || []).map((ing: any) => {
      const item = ing.inventory_item || ing.inventoryItem || {};
      return {
        item: {
          id: String(item.id || ing.inventory_item_id),
          name: item.name || ing.item_name || '',
          sku: item.sku || ing.item_sku || '',
          unit: item.unit || ing.item_unit || ing.unit || '',
          minStock: item.min_stock !== undefined ? Number(item.min_stock) : undefined,
          cost: item.cost !== undefined ? Number(item.cost) : (ing.item_cost !== undefined ? Number(ing.item_cost) : undefined),
        },
        qty: Number(ing.qty),
        unit: ing.unit || '',
        wastePct: ing.waste_pct !== undefined ? Number(ing.waste_pct) : 0,
        ingredientCost: ing.ingredient_cost !== undefined ? Number(ing.ingredient_cost) : undefined,
        notes: ing.notes ?? undefined,
      };
    }),
  };
};

export const listRecipes = async (q?: string): Promise<ProductRecipe[]> => {
  const { data } = await apiClient.get<any[]>('/inventory/recipes', { params: q ? { q } : undefined });
  return (data || []).map(mapBackendToFrontend);
};

export const getRecipe = async (productId: string): Promise<ProductRecipe> => {
  const { data } = await apiClient.get<any>(`/inventory/recipes/${productId}`);
  return mapBackendToFrontend(data);
};

export const upsertRecipe = async (payload: UpsertRecipePayload): Promise<ProductRecipe> => {
  const backendPayload = {
    product_id: Number(payload.productId),
    name: payload.name,
    ingredients: payload.ingredients.map((ing) => ({
      inventory_item_id: Number(ing.itemId),
      qty: Number(ing.qty),
      unit: ing.unit,
      waste_pct: Number(ing.wastePct ?? 0),
      notes: ing.notes,
    })),
  };
  const { data } = await apiClient.post<any>('/inventory/recipes', backendPayload);
  return mapBackendToFrontend(data);
};
